import React from 'react';
import { getActionPlan, saveActionPlan, beaconActionPlan } from '../services/adminService';

/**
 * 100-day action plan — React port of components/ActionPlan.dc.html.
 *
 * The .dc.html is a Claude Design Component and is kept as the DESIGN
 * REFERENCE ONLY. It is never loaded or embedded at runtime; this file is the
 * implementation. When the design changes, port the change here by hand — the
 * repo deliberately ships no DC runtime for one component.
 *
 * Mapping used for the port, if you need to diff the two:
 *   renderVals()          -> render(), values used inline instead of returned
 *   sc-if                 -> {cond && <jsx/>}
 *   sc-for                -> {arr.map(...)}
 *   sc-camel-on-click     -> onClick
 *   sc-camel-view-box     -> viewBox
 *   {{ value }}           -> {value}
 *   this.props.X          -> this.props.X (unchanged; defaults preserved)
 *
 * All logic — geometry(), mult(), shieldAtOf(), streakCalc(), toggleMission(),
 * missionRow(), tickTo() — is carried over unchanged apart from persistence.
 *
 * Persistence differs from the design source on purpose. The design saved to
 * localStorage and had a "Reset demo progress" link; both are gone. Progress
 * now lives in Supabase keyed by the order UUID, so it survives a device
 * change and is the same record the customer sees on any browser.
 */

const STYLE_ID = 'sl-action-plan-keyframes';

// Scoped to .sl-ap so the keyframes and the reset cannot leak into the rest of
// the delivery page, which is Tailwind-styled.
const KEYFRAMES = `
@keyframes slPop{0%{transform:scale(.4)}60%{transform:scale(1.18)}100%{transform:scale(1)}}
@keyframes slRise{0%{opacity:1;transform:translateY(0) scale(.7)}100%{opacity:0;transform:translateY(-42px) scale(1.3)}}
@keyframes slPulse{0%,100%{opacity:.25}50%{opacity:.85}}
@keyframes slFadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}
@keyframes slFadeIn{from{opacity:0}to{opacity:1}}
@keyframes slShieldIn{0%{transform:scale(.2) rotate(-10deg);opacity:0}55%{transform:scale(1.15) rotate(3deg);opacity:1}100%{transform:scale(1) rotate(0deg)}}
.sl-ap a{color:#7c3aed;cursor:pointer}
.sl-ap a:hover{color:#6d28d9}
.sl-ap textarea:focus{border-color:#8b5cf6}
`;

// Design tokens the component references. The delivery page does not load the
// marketing design system, so the ones actually used are declared here rather
// than assumed to be present globally.
const T = {
  purple900: '#4c1d95', purple800: '#5b21b6', purple700: '#6d28d9',
  purple600: '#7c3aed', purple500: '#8b5cf6', purple300: '#c4b5fd',
  purple200: '#ddd6fe', purple100: '#ede9fe', purple50: '#f6f4fd',
  ink900: '#1a1130', ink800: '#2b2440', ink700: '#4b4560', ink600: '#5b5470',
  ink500: '#6b6580', ink400: '#8a7fb0', ink300: '#9691a8',
  neutral100: '#f2f1f8', neutral200: '#e2dfeb', neutral300: '#d8d5e2',
  borderLilac: '#efe9fb', borderLilacStrong: '#e2d9f6', borderNeutral: '#efedf6',
  amber100: '#fef3c7', amber800: '#92400e',
  gradCta: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
  gradInset: 'linear-gradient(160deg, #f5f1fe, #ede7fc)',
  gradDarkPanel: 'radial-gradient(800px 400px at 50% 0%, #4a2691 0%, #1a0c38 75%)',
  shadowCard: '0 12px 32px rgba(30, 15, 60, 0.05)',
  shadowCardMd: '0 18px 44px rgba(30, 15, 60, 0.07)',
  shadowDarkPanel: '0 24px 60px rgba(26, 12, 56, 0.4)',
  shadowCtaSm: '0 8px 20px rgba(124, 58, 237, 0.4)',
};

const FONT = 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const CheckIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#fff" />
  </svg>
);

const BackArrow = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
  </svg>
);

const ArrowIcon = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, flexShrink: 0 }}>
    <path d="M5 12h14m-7-7l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// The design source used <x-import ... Button variant="primary">. Ported to a
// plain button matching the primary CTA in NewLanding.jsx (gradient, radius 14,
// weight 700, arrow) so the delivery page carries no design-system dependency.
const PrimaryButton = ({ onClick, children, withArrow = true }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 9, background: T.gradCta, color: '#fff', padding: '15px 24px',
      borderRadius: 14, fontSize: 15, fontWeight: 700, border: 'none',
      cursor: 'pointer', boxShadow: '0 12px 26px rgba(124,58,237,.35)',
      fontFamily: 'inherit', transition: 'transform .18s, box-shadow .18s',
    }}
  >
    {children}{withArrow && <ArrowIcon size={17} />}
  </button>
);

const SAVE_DEBOUNCE_MS = 1200;

class ActionPlan extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      plan: null, screen: 'home', view: null, progress: {}, stars: 0, disp: 0,
      expWeek: null, howOpen: false, pasteFor: '', pasteText: '', fx: '',
      copied: -1, pathReveal: false, celebrate: null,
      startedAt: null, loadError: '',
    };
    this._geo = null;
    this._saveTimer = null;
    this._saving = false;
    this._pendingSave = null;   // last state seen while a save was in flight
    this._mounted = false;
  }

  // ── lifecycle ────────────────────────────────────────────────────────────
  componentDidMount() {
    this._mounted = true;
    this._injectKeyframes();
    this._load();
    this._unload = () => this._flushBeacon();
    window.addEventListener('beforeunload', this._unload);
    // pagehide fires in cases beforeunload does not (bfcache, mobile Safari).
    window.addEventListener('pagehide', this._unload);
  }

  componentWillUnmount() {
    this._mounted = false;
    window.removeEventListener('beforeunload', this._unload);
    window.removeEventListener('pagehide', this._unload);
    clearTimeout(this._saveTimer);
    clearTimeout(this._pr);
    clearTimeout(this._fxT);
    clearTimeout(this._cbT);
    clearTimeout(this._cpT);
    cancelAnimationFrame(this._raf);
    // Leaving the tab is a real save point — the debounce may still be pending.
    this._flushBeacon();
  }

  _injectKeyframes() {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = KEYFRAMES;
    document.head.appendChild(el);
  }

  /**
   * Decision 5a: the plan JSON path is published on window.__resources by the
   * host page in a useEffect, never via dangerouslySetInnerHTML — React does
   * not execute <script> tags inserted that way, so the design source's
   * inline-script approach would have left __resources undefined.
   */
  async _load() {
    const { orderId } = this.props;
    try {
      const saved = await getActionPlan(orderId);
      const res = (typeof window !== 'undefined' && window.__resources) || {};
      const url = (res.planData && res.planData[saved.track])
        || `/data/${saved.track || 'entry_mid'}.json`;

      const r = await fetch(url);
      if (!r.ok) throw new Error(`Could not load plan content (${r.status})`);
      const plan = await r.json();

      if (!this._mounted) return;
      if (!plan || !Array.isArray(plan.weeks) || plan.weeks.length === 0) {
        this.setState({ loadError: 'empty' });
        return;
      }

      const stars = saved.stars || 0;
      this.setState({
        plan, progress: saved.progress || {}, stars, disp: stars,
        startedAt: saved.started_at || null,
      });
      clearTimeout(this._pr);
      this._pr = setTimeout(() => this._mounted && this.setState({ pathReveal: true }), 250);
    } catch (e) {
      if (this._mounted) this.setState({ loadError: e.message || 'Could not load your plan.' });
    }
  }

  // ── persistence ──────────────────────────────────────────────────────────
  /**
   * Decision 5c: defer, never drop. The design source's `if (this._saving)
   * return;` silently discarded any write that landed during an in-flight
   * request, so the last tick before closing the tab could vanish. Here a save
   * arriving mid-flight is stashed in _pendingSave and replayed on completion,
   * so the server always converges on the latest state.
   */
  _scheduleSave() {
    clearTimeout(this._saveTimer);
    this._saveTimer = setTimeout(() => this._save(), SAVE_DEBOUNCE_MS);
  }

  async _save() {
    const snapshot = { progress: this.state.progress, stars: this.state.stars };
    if (this._saving) {
      this._pendingSave = snapshot;
      return;
    }
    this._saving = true;
    try {
      const res = await saveActionPlan(this.props.orderId, snapshot.progress, snapshot.stars);
      if (this._mounted && res && res.started_at && !this.state.startedAt) {
        this.setState({ startedAt: res.started_at });
      }
    } catch (e) {
      // Keep the newest state queued so the next tick retries it rather than
      // losing the write. Progress is not money — a failed save is not worth
      // interrupting the customer with an error.
      this._pendingSave = { progress: this.state.progress, stars: this.state.stars };
    } finally {
      this._saving = false;
      const pending = this._pendingSave;
      this._pendingSave = null;
      if (pending && this._mounted) this._save();
    }
  }

  /**
   * First-open welcome card, dismissed. 'welcomed' lives alongside the
   * mission-id arrays in the same progress jsonb column — it is the one key
   * in that object that is a bool, not a list of completed mission ids.
   * Saved immediately rather than through _scheduleSave: the debounce exists
   * to coalesce rapid mission taps, not a one-time onboarding action, and the
   * card should not be able to reappear on a refresh before the debounce
   * would otherwise have fired.
   */
  _dismissWelcome() {
    clearTimeout(this._saveTimer);
    this.setState(
      { progress: { ...this.state.progress, welcomed: true } },
      () => this._save()
    );
  }

  /**
   * Decision 5d: unload-time save via sendBeacon. An async fetch started in
   * beforeunload is routinely abandoned; a beacon is handed to the browser and
   * delivered out-of-band, and cannot delay the unload.
   */
  _flushBeacon() {
    if (!this.state.plan) return;
    clearTimeout(this._saveTimer);
    beaconActionPlan(this.props.orderId, this.state.progress, this.state.stars);
  }

  // ── plan maths (ported unchanged from the design source) ─────────────────
  startedAt() {
    if (this.state.startedAt) {
      const d = new Date(this.state.startedAt);
      if (!isNaN(d)) { d.setHours(0, 0, 0, 0); return d; }
    }
    const d = new Date(); d.setHours(0, 0, 0, 0);
    return d;
  }

  // Day 1 is the day the plan was first saved; before that it is day 1.
  calDay() {
    const start = this.startedAt();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.floor((today - start) / 864e5);
    return Math.max(1, Math.min(100, diff + 1));
  }

  seed() { return Math.floor(this.startedAt().getTime() / 864e5) % 997; }

  weekOf(d) { return Math.floor((d - 1) / 5) + 1; }
  wdOf(d) { return ((d - 1) % 5) + 1; }
  curWeek() { return Math.min(14, Math.floor((this.calDay() - 1) / 7) + 1); }
  dayObj(n) {
    const w = this.state.plan.weeks[this.weekOf(n) - 1];
    return w ? w.days[this.wdOf(n) - 1] : null;
  }

  mult(jsonDay) {
    const w = this.weekOf(jsonDay), wd = this.wdOf(jsonDay), s = this.seed();
    const two = ((s + w * 3) % 5) + 1;
    const f = Math.ceil(w / 2);
    const tw = ((s + f * 5) % 2 === 0) ? f * 2 - 1 : f * 2;
    const td = ((s + f * 7) % 5) + 1;
    if (w === tw && wd === td) return 3;
    if (wd === two) return 2;
    return 1;
  }

  tickTo(target) {
    cancelAnimationFrame(this._raf);
    const start = this.state.disp, t0 = performance.now(), dur = 650;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const v = Math.round(start + (target - start) * (1 - Math.pow(1 - p, 3)));
      if (!this._mounted) return;
      this.setState({ disp: v });
      if (p < 1) this._raf = requestAnimationFrame(step);
    };
    this._raf = requestAnimationFrame(step);
  }

  ctxFor(view) {
    const plan = this.state.plan, cw = this.curWeek();
    if (view.t === 'w') {
      const wk = plan.weeks[view.n - 1], we = wk.weekend;
      return {
        key: 'w' + view.n, week: view.n, past: view.n < cw,
        mult: we.multiplier || 1, bonus: 0, missions: [we], allIds: [we.id],
      };
    }
    const day = this.dayObj(view.n), all = [...day.habits, ...day.practice];
    return {
      key: 'd' + view.n, week: this.weekOf(view.n), past: this.weekOf(view.n) < cw,
      mult: this.mult(view.n), bonus: day.bonus || 0, missions: all,
      allIds: all.map(m => m.id),
    };
  }

  toggleMission(ctx, m) {
    const cur = this.state.progress[ctx.key] || [], has = cur.includes(m.id);
    let ns = this.state.stars, arr;
    const wasFull = ctx.allIds.length > 0 && ctx.allIds.every(id => cur.includes(id));
    if (has) {
      arr = cur.filter(i => i !== m.id);
      if (!ctx.past) { ns -= m.stars * ctx.mult; if (wasFull) ns -= ctx.bonus * ctx.mult; }
    } else {
      arr = [...cur, m.id];
      if (!ctx.past) {
        ns += m.stars * ctx.mult;
        if (ctx.allIds.every(id => arr.includes(id))) ns += ctx.bonus * ctx.mult;
      }
      this.setState({ fx: ctx.key + ':' + m.id });
      clearTimeout(this._fxT);
      this._fxT = setTimeout(() => this._mounted && this.setState({ fx: '' }), 1000);
    }
    const newProg = { ...this.state.progress, [ctx.key]: arr };
    let celebrate = this.state.celebrate;
    if (!has && !ctx.past && ctx.week === this.curWeek()) {
      const at = this.shieldAtOf(ctx.week);
      if (this.weekStarsWith(this.state.progress, ctx.week) < at
        && this.weekStarsWith(newProg, ctx.week) >= at) {
        celebrate = ctx.week;
        clearTimeout(this._cbT);
        this._cbT = setTimeout(() => this._mounted && this.setState({ celebrate: null }), 3600);
      }
    }
    this.setState({ progress: newProg, stars: ns, pasteFor: '', pasteText: '', celebrate });
    this.tickTo(ns);
    this._scheduleSave();
  }

  earnedOfDay(n, prog) {
    const ids = (prog || this.state.progress)['d' + n] || [];
    if (!ids.length) return 0;
    const day = this.dayObj(n), all = [...day.habits, ...day.practice], m = this.mult(n);
    let s = 0;
    all.forEach(ms => { if (ids.includes(ms.id)) s += ms.stars * m; });
    if (all.length && all.every(ms => ids.includes(ms.id))) s += (day.bonus || 0) * m;
    return s;
  }

  weekStarsWith(prog, w) {
    const wk = this.state.plan.weeks[w - 1];
    let s = 0;
    wk.days.forEach(d => { s += this.earnedOfDay(d.day, prog); });
    if (wk.weekend && (prog['w' + w] || []).includes(wk.weekend.id)) {
      s += wk.weekend.stars * (wk.weekend.multiplier || 1);
    }
    return s;
  }

  shieldAtOf(w) {
    const wk = this.state.plan.weeks[w - 1];
    if (wk.shieldAt) return wk.shieldAt;
    const avail = wk.days.reduce((a, d) =>
      a + [...d.habits, ...d.practice].reduce((x, m) => x + m.stars, 0) + (d.bonus || 0), 0)
      + (wk.weekend ? wk.weekend.stars * (wk.weekend.multiplier || 1) : 0);
    return Math.round(avail * 0.60 / 5) * 5;
  }

  shieldDef(w) {
    const tier = w <= 2 ? 0 : w <= 5 ? 1 : w <= 8 ? 2 : w <= 11 ? 3 : 4;
    const wk = this.state.plan.weeks[w - 1];
    return {
      tier,
      name: (wk && wk.shieldName)
        || ['Starter shield', 'Star shield', 'Chevron shield', 'Laurel shield', 'Crown shield'][tier],
      fill: ['#8b5cf6', '#7c3aed', '#6d28d9', '#5b21b6', '#4c1d95'][tier],
      star: tier >= 1, chev: tier >= 2, laur: tier >= 3, crown: tier >= 4,
    };
  }

  streakCalc() {
    let streak = 0, missed = 0, lastW = 0;
    for (let cd = 1; cd <= this.calDay(); cd++) {
      const w = Math.floor((cd - 1) / 7) + 1, dow = cd - (w - 1) * 7;
      if (dow > 5) continue;
      if (w !== lastW) { missed = 0; lastW = w; }
      const jd = (w - 1) * 5 + dow;
      const active = (this.state.progress['d' + jd] || []).length > 0;
      if (active) streak++;
      else { missed++; if (missed >= 3) streak = 0; }
    }
    return streak;
  }

  fmtD(d) {
    return d.getDate() + ' ' + ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
  }

  weekRange(w) {
    const a = new Date(this.startedAt()); a.setDate(a.getDate() + (w - 1) * 7);
    const b = new Date(a); b.setDate(b.getDate() + 6);
    if (a.getMonth() === b.getMonth()) return a.getDate() + '–' + this.fmtD(b);
    return this.fmtD(a) + ' – ' + this.fmtD(b);
  }

  geometry() {
    if (this._geo) return this._geo;
    const N = 120, poly = [], cum = [0];
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      const x = 18 + 322 * t;
      const y = 182 - 142 * t + 15 * Math.sin(t * Math.PI * 2.2) * (1 - t * 0.35);
      poly.push([x, y]);
      if (i > 0) { const [px, py] = poly[i - 1]; cum.push(cum[i - 1] + Math.hypot(x - px, y - py)); }
    }
    const d = 'M' + poly.map(p => p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' L ');
    const hill = d + ' L 340 212 L 18 212 Z';
    const marks = [];
    for (let i = 0; i < 14; i++) {
      const idx = Math.round((i / 13) * N);
      marks.push({ x: poly[idx][0], y: poly[idx][1], len: cum[idx] });
    }
    this._geo = { d, hill, marks, total: cum[N], end: poly[N] };
    return this._geo;
  }

  starPts(cx, cy, r) {
    const pts = [];
    for (let i = 0; i < 10; i++) {
      const a = -Math.PI / 2 + i * Math.PI / 5, rr = i % 2 === 0 ? r : r * 0.44;
      pts.push((cx + rr * Math.cos(a)).toFixed(1) + ',' + (cy + rr * Math.sin(a)).toFixed(1));
    }
    return pts.join(' ');
  }

  nav(view) {
    this._save();
    this.setState({ screen: 'day', view, pasteFor: '', pasteText: '' });
    window.scrollTo(0, 0);
  }

  missionRow(ctx, m) {
    const done = (this.state.progress[ctx.key] || []).includes(m.id);
    const fxKey = ctx.key + ':' + m.id;
    const animMode = this.props.completeAnim || 'Star fly';
    const earned = ctx.past ? 0 : m.stars * ctx.mult;
    return {
      id: m.id, text: m.text, stars: m.stars, paste: m.paste,
      done, notDone: !done,
      op: done ? 0.55 : 1,
      anim: (this.state.fx === fxKey) ? 'slPop .45s cubic-bezier(.16,1,.3,1)' : 'none',
      fly: this.state.fx === fxKey && animMode === 'Star fly',
      earnedLabel: ctx.past ? '★ 0' : '+' + earned + ' ★',
      pasteOpen: this.state.pasteFor === fxKey,
      tap: () => {
        if (!done && m.paste) {
          this.setState({ pasteFor: this.state.pasteFor === fxKey ? '' : fxKey, pasteText: '' });
          return;
        }
        this.toggleMission(ctx, m);
      },
      confirm: () => { if (this.state.pasteText.trim().length >= 15) this.toggleMission(ctx, m); },
    };
  }

  // ── render helpers ───────────────────────────────────────────────────────
  renderMissionList(missions, keyPrefix) {
    const pasteShort = this.state.pasteText.trim().length < 15;
    const pasteBtnOp = pasteShort ? 0.4 : 1;
    return missions.map((m, i) => (
      <div key={keyPrefix + m.id + i} style={{ borderTop: `1px solid ${T.borderNeutral}`, position: 'relative' }}>
        <div onClick={m.tap} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 0', cursor: 'pointer' }}>
          {m.done && (
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: T.gradCta, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', animation: m.anim }}>
              <CheckIcon size={14} />
            </div>
          )}
          {m.notDone && (
            <div style={{ width: 26, height: 26, borderRadius: '50%', border: `2px solid ${T.neutral300}`, flex: 'none' }} />
          )}
          <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: T.ink800, opacity: m.op }}>{m.text}</span>
          {m.done && <span style={{ fontSize: 12.5, fontWeight: 800, color: '#d97706' }}>{m.earnedLabel}</span>}
          {m.notDone && (
            <span style={{ fontSize: 11.5, fontWeight: 700, color: '#b45309', background: T.amber100, borderRadius: 100, padding: '3px 10px' }}>★ {m.stars}</span>
          )}
          {m.fly && (
            <>
              <span style={{ position: 'absolute', right: 16, top: 4, color: '#fbbf24', fontSize: 16, pointerEvents: 'none', animation: 'slRise .9s ease-out forwards' }}>★</span>
              <span style={{ position: 'absolute', right: 44, top: 10, color: '#fbbf24', fontSize: 11, pointerEvents: 'none', animation: 'slRise .9s ease-out .12s forwards' }}>★</span>
            </>
          )}
        </div>
        {m.pasteOpen && (
          <div style={{ padding: '0 0 16px' }}>
            <textarea
              value={this.state.pasteText}
              onChange={(e) => this.setState({ pasteText: e.target.value })}
              placeholder={m.paste}
              rows={4}
              style={{ width: '100%', boxSizing: 'border-box', border: `1.5px solid ${T.borderLilacStrong}`, borderRadius: 12, padding: 12, fontFamily: 'inherit', fontSize: 13.5, lineHeight: 1.6, color: T.ink800, background: '#fdfcff', resize: 'vertical', outline: 'none' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 8 }}>
              <span style={{ fontSize: 11.5, color: T.ink300, lineHeight: 1.4 }}>Nothing is saved. This just confirms you did it.</span>
              <button
                type="button"
                onClick={m.confirm}
                disabled={pasteShort}
                style={{ border: 0, background: T.gradCta, color: '#fff', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, borderRadius: 100, padding: '9px 16px', cursor: pasteShort ? 'default' : 'pointer', opacity: pasteBtnOp, flex: 'none' }}
              >
                Done · ★ {m.stars}
              </button>
            </div>
          </div>
        )}
      </div>
    ));
  }

  renderMarker(mk, i) {
    const dark = (this.props.homeLayout || 'Summit hero') === 'Summit hero';
    return (
      <g key={i} onClick={mk.go} style={{ cursor: 'pointer' }}>
        {mk.doneP && (
          <>
            <circle cx={mk.x} cy={mk.y} r="8.5" fill={dark ? '#8b5cf6' : '#7c3aed'} />
            <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#ffffff" transform={mk.tf} />
          </>
        )}
        {mk.doneS && (
          <g transform={mk.sTf}>
            <path d="M0 -7.5 L6.4 -4.8 V0.6 C6.4 4.8 3.3 7 0 8.6 C-3.3 7 -6.4 4.8 -6.4 0.6 V-4.8 Z"
              fill={mk.sFill} stroke={dark ? 'rgba(255,255,255,.8)' : '#ffffff'} strokeWidth="1.1" />
            {mk.sStar && <polygon points={mk.sStarPts} fill="#fbbf24" />}
            {mk.sChev && <path d="M-3.6 3.4 L0 5.6 L3.6 3.4" fill="none" stroke="#fbbf24" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />}
            {mk.sLaur && (
              <>
                <path d="M-8.4 4.5 C-10.2 1.5 -10.2 -2.5 -8.4 -5.5" fill="none" stroke={dark ? '#fbbf24' : '#f59e0b'} strokeWidth="1.2" strokeLinecap="round" />
                <path d="M8.4 4.5 C10.2 1.5 10.2 -2.5 8.4 -5.5" fill="none" stroke={dark ? '#fbbf24' : '#f59e0b'} strokeWidth="1.2" strokeLinecap="round" />
              </>
            )}
            {mk.sCrown && <path d="M-4.2 -9 L-2.6 -11.6 L0 -9.4 L2.6 -11.6 L4.2 -9 Z" fill={dark ? '#fbbf24' : '#f59e0b'} />}
          </g>
        )}
        {mk.cur && (
          <>
            <circle cx={mk.x} cy={mk.y} r="14" fill={dark ? 'rgba(251,191,36,.3)' : 'rgba(251,191,36,.32)'} style={{ animation: 'slPulse 2.2s infinite' }} />
            <polygon points={mk.star} fill={dark ? '#fbbf24' : '#f59e0b'} />
            <text x={mk.x} y={mk.ly} textAnchor="middle" fontSize="10" fontWeight="700"
              fill={dark ? '#fbbf24' : '#b45309'} fontFamily="Poppins">Week {mk.weekNum}</text>
          </>
        )}
        {mk.fut && <circle cx={mk.x} cy={mk.y} r="4.5" fill={dark ? 'rgba(255,255,255,.25)' : '#d8d5e2'} />}
      </g>
    );
  }

  renderPathSvg(geo, doneDash, flagTf, markers, dark) {
    return (
      <svg viewBox="0 0 360 212" style={{ width: '100%', display: 'block', marginTop: dark ? 8 : 0 }}>
        <path d={geo.hill} fill={dark ? 'rgba(255,255,255,.05)' : 'rgba(124,58,237,.055)'} />
        <path d={geo.d} fill="none" stroke={dark ? 'rgba(255,255,255,.18)' : '#e2dfeb'} strokeWidth="3" strokeLinecap="round" strokeDasharray="1 8" />
        <path d={geo.d} fill="none" stroke={dark ? '#c4b5fd' : '#8b5cf6'} strokeWidth="3.5" strokeLinecap="round"
          strokeDasharray={doneDash} style={{ transition: 'stroke-dasharray 1.3s cubic-bezier(.16,1,.3,1)' }} />
        <g transform={flagTf}>
          <path d="M0 0 v-24" stroke={dark ? 'rgba(255,255,255,.55)' : '#c5bfe0'} strokeWidth="2.4" strokeLinecap="round" />
          <path d="M0 -24 l15 4.5 -15 4.5 z" fill="#fbbf24" />
        </g>
        {markers.map((mk, i) => this.renderMarker(mk, i))}
      </svg>
    );
  }

  // ── render ───────────────────────────────────────────────────────────────
  render() {
    const plan = this.state.plan;

    if (this.state.loadError === 'empty') {
      return (
        <div className="sl-ap" style={{ fontFamily: FONT, padding: '60px 20px', textAlign: 'center', color: T.ink500 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.ink900, marginBottom: 6 }}>Your 100-day plan is being prepared</div>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>The lesson content for your track is not published yet. It will appear here as soon as it is — nothing you need to do.</div>
        </div>
      );
    }
    if (this.state.loadError) {
      return (
        <div className="sl-ap" style={{ fontFamily: FONT, padding: '60px 20px', textAlign: 'center', color: T.ink500 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Could not load your plan</div>
          <div style={{ fontSize: 12.5 }}>{this.state.loadError}</div>
        </div>
      );
    }
    if (!plan) {
      return (
        <div className="sl-ap" style={{ fontFamily: FONT, padding: '80px 0', textAlign: 'center', color: T.ink300, fontSize: 13 }}>
          Loading your plan…
        </div>
      );
    }

    // First-open welcome card. 'progress' being empty (no keys at all, not
    // just missing 'welcomed') is what limits this to a true first open —
    // any account with real mission history predates this card and should
    // never see it retroactively.
    const progressIsEmpty = Object.keys(this.state.progress).length === 0;
    if (this.state.screen === 'home' && progressIsEmpty && !this.state.progress.welcomed) {
      const firstName = (this.props.userName || '').trim().split(' ')[0] || 'there';
      return (
        <div className="sl-ap" style={{ fontFamily: FONT }}>
          <div style={{
            position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(26,17,48,.72)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20, overflowY: 'auto', animation: 'slFadeIn .35s ease',
          }}>
            <div style={{
              background: '#fff', borderRadius: 24, boxShadow: T.shadowCardMd,
              maxWidth: 420, width: '100%', padding: '34px 26px 26px', textAlign: 'center',
              animation: 'slFadeUp .45s cubic-bezier(.16,1,.3,1)',
            }}>
              <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.4px', color: T.ink900, lineHeight: 1.32, marginBottom: 16 }}>
                Hi {firstName}, your 100-day action plan is ready.
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.65, color: T.ink600, textAlign: 'left' }}>
                <p style={{ margin: '0 0 14px' }}>We did not just rewrite your LinkedIn profile. We built you a 100-day system to land your dream job.</p>
                <p style={{ margin: '0 0 14px' }}>Every day: one short lesson, a few actions, and insider tips most candidates never see. Do them and your visibility goes up 4X.</p>
                <p style={{ margin: '0 0 14px' }}>
                  Each day you show up, you earn stars.<br />
                  Each week you hit your target, you earn a shield.<br />
                  By day 100, you will have built a pipeline most people spend years trying to figure out.
                </p>
                <p style={{ margin: 0, fontWeight: 600, color: T.ink900 }}>One action a day. That is all it takes.</p>
              </div>
              <div style={{ marginTop: 24 }}>
                <PrimaryButton withArrow={false} onClick={() => this._dismissWelcome()}>Start Day 1 →</PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const cw = this.curWeek(), calDay = this.calDay();
    const dow = calDay - (cw - 1) * 7;
    const todayNum = dow <= 5 ? (cw - 1) * 5 + dow : null;
    const geo = this.geometry();
    const layout = this.props.homeLayout || 'Summit hero';
    const isSummit = layout === 'Summit hero';
    const isDaylight = layout === 'Daylight';
    const isTodayFirst = layout === 'Today first';
    const isLightPath = isDaylight || isTodayFirst;
    const showTodayCard = !isTodayFirst;

    const markers = geo.marks.map((mk, i) => {
      const wn = i + 1;
      const st = wn < cw ? 'done' : (wn === cw ? 'cur' : 'fut');
      const sd = this.shieldDef(wn);
      const hasShield = st === 'done' && this.weekStarsWith(this.state.progress, wn) >= this.shieldAtOf(wn);
      return {
        x: mk.x.toFixed(1), y: mk.y.toFixed(1), ly: (mk.y - 22).toFixed(1), weekNum: cw,
        doneP: st === 'done' && !hasShield, doneS: hasShield, cur: st === 'cur', fut: st === 'fut',
        tf: 'translate(' + (mk.x - 6.4).toFixed(1) + ',' + (mk.y - 6.4).toFixed(1) + ') scale(0.53)',
        sTf: 'translate(' + mk.x.toFixed(1) + ',' + mk.y.toFixed(1) + ') scale(1.08)',
        sFill: sd.fill, sStar: sd.star, sStarPts: this.starPts(0, 0.9, 3.4),
        sChev: sd.chev, sLaur: sd.laur, sCrown: sd.crown,
        star: this.starPts(mk.x, mk.y, 9.5),
        go: () => { this.setState({ screen: 'weeks', expWeek: wn }); window.scrollTo(0, 0); },
      };
    });
    const doneLen = this.state.pathReveal ? geo.marks[cw - 1].len : 0;
    const doneDash = doneLen.toFixed(1) + ' ' + (geo.total + 10).toFixed(1);
    const flagTf = 'translate(' + geo.end[0].toFixed(1) + ',' + (geo.end[1] - 4).toFixed(1) + ')';

    // today card
    const todayDay = todayNum ? this.dayObj(todayNum) : null;
    const todayWeekend = !todayNum ? plan.weeks[cw - 1].weekend : null;
    const tMissions = todayDay ? [...todayDay.habits, ...todayDay.practice] : (todayWeekend ? [todayWeekend] : []);
    const tKey = todayNum ? 'd' + todayNum : 'w' + cw;
    const tDoneIds = this.state.progress[tKey] || [];
    const tBase = tMissions.reduce((a, m) => a + m.stars, 0) + (todayDay ? (todayDay.bonus || 0) : 0);
    const tAllDone = tMissions.length > 0 && tMissions.every(m => tDoneIds.includes(m.id));
    const todayView = todayNum ? { t: 'd', n: todayNum } : { t: 'w', n: cw };
    const todayTitle = todayDay ? todayDay.title : (todayWeekend ? todayWeekend.title : '');
    const todayMeta = tAllDone
      ? 'All done for today. Come back tomorrow.'
      : tMissions.length + (tMissions.length === 1 ? ' mission' : ' missions') + ' · ★ ' + tBase + ' to earn';
    const todayBtn = tAllDone ? 'Review today' : (tDoneIds.length > 0 ? 'Continue today' : 'Start today');

    // this week rows
    const wk = plan.weeks[cw - 1];
    const weekRows = wk.days.map((d) => {
      const ids = this.state.progress['d' + d.day] || [];
      const all = [...d.habits, ...d.practice];
      const full = all.length > 0 && all.every(m => ids.includes(m.id));
      const isToday = d.day === todayNum;
      const earned = this.earnedOfDay(d.day);
      return {
        n: this.wdOf(d.day), title: d.title,
        done: full, cur: !full && isToday, fut: !full && !isToday,
        color: full || isToday ? T.ink800 : T.ink400,
        tag: full ? '★ ' + earned : (isToday ? 'Today' : (earned > 0 ? '★ ' + earned : '')),
        tagColor: full || earned > 0 ? '#d97706' : T.purple600,
        go: () => this.nav({ t: 'd', n: d.day }),
      };
    });
    if (wk.weekend) {
      const wIds = this.state.progress['w' + cw] || [];
      const wDone = wIds.includes(wk.weekend.id);
      weekRows.push({
        n: 'W', title: wk.weekend.title, done: wDone, cur: false, fut: !wDone,
        color: wDone ? T.ink800 : T.ink400,
        tag: wDone ? '★ ' + (wk.weekend.stars * (wk.weekend.multiplier || 1)) : 'Weekend',
        tagColor: wDone ? '#d97706' : T.ink300,
        go: () => this.nav({ t: 'w', n: cw }),
      });
    }

    // stars this week
    let weekStars = 0;
    wk.days.forEach(d => { weekStars += this.earnedOfDay(d.day); });
    if (wk.weekend && (this.state.progress['w' + cw] || []).includes(wk.weekend.id)) {
      weekStars += wk.weekend.stars * (wk.weekend.multiplier || 1);
    }

    // weeks list
    const weeksList = plan.weeks.map(w => {
      const st = w.week < cw ? 'past' : (w.week === cw ? 'cur' : 'locked');
      const totalM = w.days.reduce((a, d) => a + d.habits.length + d.practice.length, 0) + (w.weekend ? 1 : 0);
      let doneM = 0;
      w.days.forEach(d => { doneM += (this.state.progress['d' + d.day] || []).length; });
      if (w.weekend && (this.state.progress['w' + w.week] || []).includes(w.weekend.id)) doneM++;
      const unlockD = new Date(this.startedAt()); unlockD.setDate(unlockD.getDate() + (w.week - 1) * 7);
      const open = this.state.expWeek === w.week;
      const sEarn = st === 'past' && this.weekStarsWith(this.state.progress, w.week) >= this.shieldAtOf(w.week);
      const rows = st === 'locked' ? [] : w.days.map(d => {
        const ids = this.state.progress['d' + d.day] || [];
        const all = [...d.habits, ...d.practice];
        const full = all.length > 0 && all.every(m => ids.includes(m.id));
        return {
          title: d.title, done: full, notDone: !full,
          tag: d.day === todayNum ? 'Today' : (full ? '★ ' + this.earnedOfDay(d.day) : ''),
          tagColor: full ? '#d97706' : T.purple600,
          go: () => this.nav({ t: 'd', n: d.day }),
        };
      });
      if (st !== 'locked' && w.weekend) {
        const wDone = (this.state.progress['w' + w.week] || []).includes(w.weekend.id);
        rows.push({
          title: 'Weekend — ' + w.weekend.title, done: wDone, notDone: !wDone,
          tag: wDone ? '★ ' + (w.weekend.stars * (w.weekend.multiplier || 1)) : '',
          tagColor: '#d97706',
          go: () => this.nav({ t: 'w', n: w.week }),
        });
      }
      return {
        n: w.week, title: w.title, open,
        past: st === 'past', cur: st === 'cur', locked: st === 'locked',
        op: st === 'locked' ? 0.62 : 1,
        titleColor: st === 'locked' ? T.ink400 : T.ink900,
        sub: this.weekRange(w.week) + (sEarn ? ' · Shield earned' : ''),
        tag: st === 'locked' ? 'Unlocks ' + this.fmtD(unlockD) : doneM + '/' + totalM + ' done',
        tagColor: st === 'locked' ? T.ink300 : (st === 'past' ? T.ink400 : T.purple600),
        note: st === 'past' ? 'Week closed — no stars' : (w.note || ''),
        rows,
        toggle: () => { if (st === 'locked') return; this.setState({ expWeek: open ? null : w.week }); },
      };
    });

    // day view
    let dv = null;
    if (this.state.screen === 'day' && this.state.view) {
      const view = this.state.view, ctx = this.ctxFor(view);
      const isW = view.t === 'w';
      const wkV = plan.weeks[ctx.week - 1];
      const dObj = isW ? null : this.dayObj(view.n);
      const blocks = (isW ? [{ type: 'text', content: wkV.weekend.text }] : dObj.body).map((b, i) => ({
        isText: b.type === 'text', isHeading: b.type === 'heading', isList: b.type === 'list',
        isCopy: b.type === 'copy', isTip: b.type === 'tip',
        content: b.content, items: b.items || [],
        labelText: b.label || 'Template',
        copyLabel: this.state.copied === i ? 'Copied' : 'Copy',
        copy: () => {
          try { navigator.clipboard.writeText(b.content); } catch (e) { /* clipboard unavailable */ }
          this.setState({ copied: i });
          clearTimeout(this._cpT);
          this._cpT = setTimeout(() => this._mounted && this.setState({ copied: -1 }), 1600);
        },
      }));
      const habits = isW ? [] : dObj.habits.map(m => this.missionRow(ctx, m));
      const practice = (isW ? [wkV.weekend] : dObj.practice).map(m => this.missionRow(ctx, m));
      const doneIds = this.state.progress[ctx.key] || [];
      const allDone = ctx.allIds.length > 0 && ctx.allIds.every(id => doneIds.includes(id));
      const avail = (ctx.missions.reduce((a, m) => a + m.stars, 0) + ctx.bonus) * ctx.mult;
      const dots = isW ? [] : wkV.days.map(d => {
        const ids = this.state.progress['d' + d.day] || [];
        const all = [...d.habits, ...d.practice];
        const full = all.length > 0 && all.every(m => ids.includes(m.id));
        return {
          n: this.wdOf(d.day), done: full, cur: !full && d.day === view.n, fut: !full && d.day !== view.n,
          go: () => this.nav({ t: 'd', n: d.day }),
        };
      });
      const maxDay = Math.min(70, (Math.min(14, Math.floor((calDay - 1) / 7) + 1)) * 5);
      const hasPrev = !isW && view.n > 1, hasNext = !isW && view.n < maxDay;
      dv = {
        crumb: isW ? 'Week ' + ctx.week + ' · Weekend' : 'Week ' + ctx.week + ' · Day ' + this.wdOf(view.n) + ' of 5',
        hasDots: dots.length > 0, dots,
        multBanner: ctx.mult > 1 && !ctx.past,
        multLabel: ctx.mult + '× star day',
        multText: ctx.mult === 3 ? 'Every star you earn today counts triple.' : 'Every star you earn today is doubled.',
        past: ctx.past, opacity: ctx.past ? 0.78 : 1,
        read: isW ? 'Weekend mission' : dObj.readTime,
        title: isW ? wkV.weekend.title : dObj.title,
        learn: isW ? '' : dObj.learn,
        why: isW ? '' : dObj.why,
        blocks,
        hasDoNext: !isW && dObj.doNext && dObj.doNext.length > 0,
        doNext: isW ? [] : (dObj.doNext || []).map((t, i) => ({ n: i + 1, text: t })),
        avail,
        hasHabits: habits.length > 0, habits,
        practiceLabel: isW ? 'The mission' : 'Today’s missions',
        practice,
        bonusPending: !isW && ctx.bonus > 0 && !allDone,
        bonusEarned: !isW && ctx.bonus > 0 && allDone,
        bonus: ctx.bonus,
        bonusEarnedLabel: '★ ' + (ctx.past ? 0 : ctx.bonus * ctx.mult),
        tomorrow: isW ? '' : (dObj.tomorrow || ''),
        hasPrev, hasNext,
        prevLabel: hasPrev ? 'Day ' + (view.n - 1) : '', nextLabel: hasNext ? 'Day ' + (view.n + 1) : '',
        prev: () => this.nav({ t: 'd', n: view.n - 1 }), next: () => this.nav({ t: 'd', n: view.n + 1 }),
      };
    }

    const shieldAt = this.shieldAtOf(cw);
    const shieldGot = weekStars >= shieldAt;
    const cbW = this.state.celebrate;
    const cbD = this.shieldDef(cbW || cw);
    const nextUp = [3, 6, 9, 12].find(x => x > (cbW || cw));

    const shieldLine = shieldGot
      ? 'Week ' + cw + ' shield secured'
      : '★ ' + (shieldAt - weekStars) + ' more this week to keep your shield';
    const shieldMiniFill = shieldGot ? '#fbbf24' : '#8b5cf6';
    const shieldMiniOp = shieldGot ? 1 : 0.45;

    const disp = this.state.disp;
    const isHome = this.state.screen === 'home';
    const isDay = this.state.screen === 'day' && !!dv;
    const isWeeks = this.state.screen === 'weeks';

    const howLines = [
      'Learn something new each weekday, then do it.',
      'Earn stars for every mission you complete.',
      'Some days are worth 2× or 3× — you will not know until you open them.',
      'Do at least one thing a day to keep your streak.',
      'Weeks open on schedule, but stars only count in the week they belong to.',
    ];

    const goHome = () => { this._save(); this.setState({ screen: 'home', pasteFor: '' }); window.scrollTo(0, 0); };
    const openWeeks = () => { this._save(); this.setState({ screen: 'weeks', expWeek: cw }); window.scrollTo(0, 0); };

    const card = { background: '#fff', borderRadius: 22, boxShadow: T.shadowCard, marginBottom: 16 };

    return (
      <div className="sl-ap" style={{ background: T.purple50, fontFamily: FONT, color: T.ink900 }}>
        <div style={{ maxWidth: 520, margin: '0 auto', padding: '18px 18px 72px' }}>

          {/* ============ HOME ============ */}
          {isHome && (
            <div style={{ animation: 'slFadeUp .5s cubic-bezier(.16,1,.3,1)' }}>

              {isSummit && (
                <div style={{ background: T.gradDarkPanel, borderRadius: 28, padding: '26px 18px 12px', boxShadow: T.shadowDarkPanel, marginBottom: 16 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '2.2px', color: 'rgba(255,255,255,.5)', textTransform: 'uppercase' }}>Stars earned</div>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 9, marginTop: 2 }}>
                      <span style={{ fontSize: 28, color: '#fbbf24', textShadow: '0 0 26px rgba(251,191,36,.55)' }}>★</span>
                      <span style={{ fontSize: 54, fontWeight: 800, letterSpacing: '-2px', color: '#fbbf24', lineHeight: 1.15 }}>{disp}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 7, marginTop: 9 }}>
                      <svg width="14" height="17" viewBox="-8 -9.5 16 19.5">
                        <path d="M0 -7.5 L6.4 -4.8 V0.6 C6.4 4.8 3.3 7 0 8.6 C-3.3 7 -6.4 4.8 -6.4 0.6 V-4.8 Z" fill={shieldMiniFill} opacity={shieldMiniOp} stroke="rgba(255,255,255,.7)" strokeWidth="1" />
                      </svg>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: shieldGot ? '#fbbf24' : 'rgba(255,255,255,.72)' }}>{shieldLine}</span>
                    </div>
                  </div>
                  {this.renderPathSvg(geo, doneDash, flagTf, markers, true)}
                </div>
              )}

              {isDaylight && (
                <div style={{ textAlign: 'center', padding: '10px 0 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 9 }}>
                    <span style={{ fontSize: 27, color: '#fbbf24' }}>★</span>
                    <span style={{ fontSize: 52, fontWeight: 800, letterSpacing: '-2px', color: '#d97706', lineHeight: 1.15 }}>{disp}</span>
                  </div>
                  <div style={{ fontSize: 12, color: T.ink300 }}>stars earned</div>
                </div>
              )}

              {isTodayFirst && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 2px 14px' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.3px' }}>Your 100-day plan</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span style={{ fontSize: 17, color: '#fbbf24' }}>★</span>
                      <span style={{ fontSize: 22, fontWeight: 800, color: '#d97706' }}>{disp}</span>
                    </div>
                  </div>
                  <div style={{ ...card, boxShadow: T.shadowCardMd, padding: '20px 20px 22px', marginBottom: 14 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '1.6px', textTransform: 'uppercase', color: T.purple600 }}>Today · Day {todayNum || ('W' + cw)}</div>
                    <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.4px', margin: '6px 0 2px' }}>{todayTitle}</div>
                    <div style={{ fontSize: 13, color: T.ink500, marginBottom: 14 }}>{todayMeta}</div>
                    <PrimaryButton onClick={() => this.nav(todayView)}>{todayBtn}</PrimaryButton>
                  </div>
                </>
              )}

              {isLightPath && (
                <>
                  <div style={{ background: '#fff', borderRadius: 24, boxShadow: T.shadowCardMd, padding: '14px 12px 4px', marginBottom: 16 }}>
                    {this.renderPathSvg(geo, doneDash, flagTf, markers, false)}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 7, margin: '-6px 0 14px' }}>
                    <svg width="14" height="17" viewBox="-8 -9.5 16 19.5">
                      <path d="M0 -7.5 L6.4 -4.8 V0.6 C6.4 4.8 3.3 7 0 8.6 C-3.3 7 -6.4 4.8 -6.4 0.6 V-4.8 Z" fill={shieldMiniFill} opacity={shieldMiniOp} stroke="#ffffff" strokeWidth="1" />
                    </svg>
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: shieldGot ? '#b45309' : T.ink400 }}>{shieldLine}</span>
                  </div>
                </>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
                <div style={{ background: '#fff', borderRadius: 18, boxShadow: T.shadowCard, padding: '14px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.5px' }}>{calDay}</div>
                  <div style={{ fontSize: 10.5, color: T.ink300, fontWeight: 600 }}>day of 100</div>
                </div>
                <div style={{ background: '#fff', borderRadius: 18, boxShadow: T.shadowCard, padding: '14px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.5px', color: T.purple600 }}>{this.streakCalc()}</div>
                  <div style={{ fontSize: 10.5, color: T.ink300, fontWeight: 600 }}>day streak</div>
                </div>
                <div style={{ background: '#fff', borderRadius: 18, boxShadow: T.shadowCard, padding: '14px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.5px', color: '#d97706' }}>★ {weekStars}</div>
                  <div style={{ fontSize: 10.5, color: T.ink300, fontWeight: 600 }}>stars this week</div>
                </div>
              </div>

              {showTodayCard && (
                <div style={{ ...card, boxShadow: T.shadowCardMd, padding: '20px 20px 22px' }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '1.6px', textTransform: 'uppercase', color: T.purple600 }}>Today · Day {todayNum || ('W' + cw)}</div>
                  <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.4px', margin: '6px 0 2px' }}>{todayTitle}</div>
                  <div style={{ fontSize: 13, color: T.ink500, marginBottom: 14 }}>{todayMeta}</div>
                  <PrimaryButton onClick={() => this.nav(todayView)}>{todayBtn}</PrimaryButton>
                </div>
              )}

              <div style={{ ...card, padding: '18px 20px 8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>This week</span>
                  <a onClick={openWeeks} style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }}>All 14 weeks</a>
                </div>
                {weekRows.map((r, i) => (
                  <div key={i} onClick={r.go} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderTop: `1px solid ${T.borderNeutral}`, cursor: 'pointer' }}>
                    {r.done && (
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: T.gradCta, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                        <CheckIcon size={13} />
                      </div>
                    )}
                    {r.cur && (
                      <div style={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${T.purple600}`, color: T.purple600, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', fontSize: 11, fontWeight: 700 }}>{r.n}</div>
                    )}
                    {r.fut && (
                      <div style={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${T.neutral200}`, color: T.ink300, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', fontSize: 11, fontWeight: 600 }}>{r.n}</div>
                    )}
                    <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: r.color }}>{r.title}</span>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: r.tagColor }}>{r.tag}</span>
                  </div>
                ))}
              </div>

              <div style={{ ...card, padding: '4px 20px' }}>
                <div onClick={() => this.setState({ howOpen: !this.state.howOpen })} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', cursor: 'pointer' }}>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>How it works</span>
                  <span style={{ fontSize: 20, fontWeight: 400, color: T.purple600, transform: this.state.howOpen ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform .25s', lineHeight: 1 }}>+</span>
                </div>
                {this.state.howOpen && (
                  <div style={{ padding: '0 0 16px' }}>
                    {howLines.map((h, i) => (
                      <div key={i} style={{ display: 'flex', gap: 10, padding: '5px 0', fontSize: 13, lineHeight: 1.6, color: T.ink600 }}>
                        <span style={{ color: T.purple500, flex: 'none' }}>·</span><span>{h}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ============ DAY ============ */}
          {isDay && (
            <div style={{ animation: 'slFadeUp .45s cubic-bezier(.16,1,.3,1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <a onClick={goHome} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }}>
                  <BackArrow />Home
                </a>
                <span style={{ fontSize: 12, fontWeight: 600, color: T.ink400 }}>{dv.crumb}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#d97706' }}><span style={{ color: '#fbbf24' }}>★</span> {disp}</span>
              </div>

              {dv.hasDots && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 16 }}>
                  {dv.dots.map((dt, i) => (
                    <React.Fragment key={i}>
                      {dt.done && (
                        <div onClick={dt.go} style={{ width: 34, height: 34, borderRadius: '50%', background: T.gradCta, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: T.shadowCtaSm }}>
                          <CheckIcon size={15} />
                        </div>
                      )}
                      {dt.cur && (
                        <div onClick={dt.go} style={{ width: 34, height: 34, borderRadius: '50%', border: `2.5px solid ${T.purple600}`, background: '#fff', color: T.purple600, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 13, fontWeight: 800 }}>{dt.n}</div>
                      )}
                      {dt.fut && (
                        <div onClick={dt.go} style={{ width: 34, height: 34, borderRadius: '50%', border: `2px solid ${T.neutral200}`, background: '#fff', color: T.ink300, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{dt.n}</div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}

              {dv.multBanner && (
                <div style={{ background: T.amber100, borderRadius: 16, padding: '12px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, animation: 'slFadeUp .5s' }}>
                  <span style={{ fontSize: 19, color: '#f59e0b' }}>★</span>
                  <div>
                    <span style={{ fontSize: 13.5, fontWeight: 800, color: T.amber800 }}>{dv.multLabel}</span>
                    <span style={{ fontSize: 12.5, color: T.amber800, opacity: .8, display: 'block' }}>{dv.multText}</span>
                  </div>
                </div>
              )}
              {dv.past && (
                <div style={{ background: T.neutral100, borderRadius: 14, padding: '10px 16px', marginBottom: 14, fontSize: 12.5, fontWeight: 600, color: T.ink400 }}>
                  Week closed — no stars. You can still read and tick things off.
                </div>
              )}

              <div style={{ ...card, padding: '24px 22px', opacity: dv.opacity }}>
                <span style={{ display: 'inline-block', background: T.neutral100, borderRadius: 100, padding: '4px 12px', fontSize: 11, fontWeight: 600, color: T.ink500 }}>{dv.read}</span>
                <h1 style={{ fontSize: 23, fontWeight: 800, letterSpacing: '-0.5px', lineHeight: 1.25, margin: '12px 0 8px' }}>{dv.title}</h1>
                <p style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.55, color: T.ink700, margin: '0 0 14px' }}>{dv.learn}</p>
                {dv.why && (
                  <div style={{ background: T.gradInset, borderRadius: 14, padding: '12px 16px', marginBottom: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: T.purple600, marginBottom: 3 }}>Why it matters</div>
                    <div style={{ fontSize: 13.5, lineHeight: 1.65, color: T.ink600 }}>{dv.why}</div>
                  </div>
                )}
                {dv.blocks.map((b, i) => (
                  <React.Fragment key={i}>
                    {b.isText && <p style={{ fontSize: 14.5, lineHeight: 1.75, color: T.ink600, margin: '14px 0 0' }}>{b.content}</p>}
                    {b.isHeading && <h3 style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.2px', margin: '24px 0 0', color: T.ink900 }}>{b.content}</h3>}
                    {b.isList && (
                      <div style={{ margin: '12px 0 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {b.items.map((it, j) => (
                          <div key={j} style={{ display: 'flex', gap: 10, fontSize: 14, lineHeight: 1.6, color: T.ink600 }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.purple500, flex: 'none', marginTop: 8 }} /><span>{it}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {b.isCopy && (
                      <div style={{ border: `1px solid ${T.borderLilacStrong}`, background: '#faf9fd', borderRadius: 14, margin: '14px 0 0', overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 8px 8px 14px', borderBottom: `1px solid ${T.borderLilac}` }}>
                          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', color: T.ink400 }}>{b.labelText}</span>
                          <button type="button" onClick={b.copy} style={{ border: 0, background: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 700, color: T.purple600, fontFamily: 'inherit', padding: '3px 8px' }}>{b.copyLabel}</button>
                        </div>
                        <pre style={{ margin: 0, padding: '12px 14px', fontFamily: 'ui-monospace,Menlo,Consolas,monospace', fontSize: 12.5, lineHeight: 1.65, color: T.ink800, whiteSpace: 'pre-wrap' }}>{b.content}</pre>
                      </div>
                    )}
                    {b.isTip && (
                      <div style={{ background: T.gradInset, borderRadius: 16, padding: '14px 16px', margin: '18px 0 0' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: T.purple600, marginBottom: 4 }}>Tip</div>
                        <div style={{ fontSize: 13.5, lineHeight: 1.65, color: T.ink700, fontWeight: 500 }}>{b.content}</div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
                {dv.hasDoNext && (
                  <>
                    <h3 style={{ fontSize: 16, fontWeight: 800, margin: '26px 0 10px', color: T.ink900 }}>Do next</h3>
                    {dv.doNext.map((dn, i) => (
                      <div key={i} style={{ display: 'flex', gap: 12, padding: '7px 0', fontSize: 14, lineHeight: 1.6, color: T.ink600 }}>
                        <span style={{ width: 22, height: 22, borderRadius: '50%', background: T.purple100, color: T.purple700, fontSize: 11.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', marginTop: 1 }}>{dn.n}</span>
                        <span>{dn.text}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '0 4px 10px' }}>
                <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.2px' }}>Earn today</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: '#d97706' }}><span style={{ color: '#fbbf24' }}>★</span> {dv.avail} on offer</span>
              </div>

              {dv.hasHabits && (
                <div style={{ background: '#fff', borderRadius: 20, boxShadow: T.shadowCard, padding: '6px 18px', marginBottom: 12 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: T.ink400, padding: '12px 0 2px' }}>Daily habits</div>
                  {this.renderMissionList(dv.habits, 'h')}
                </div>
              )}

              <div style={{ background: '#fff', borderRadius: 20, boxShadow: T.shadowCard, padding: '6px 18px', marginBottom: 14 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: T.ink400, padding: '12px 0 2px' }}>{dv.practiceLabel}</div>
                {this.renderMissionList(dv.practice, 'p')}
                {dv.bonusPending && (
                  <div style={{ borderTop: `1px solid ${T.borderNeutral}`, padding: '12px 0 14px', fontSize: 12.5, fontWeight: 600, color: T.ink300, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 15, color: T.neutral300 }}>★</span>Finish everything today · bonus ★ {dv.bonus}
                  </div>
                )}
                {dv.bonusEarned && (
                  <div style={{ borderTop: `1px solid ${T.borderNeutral}`, padding: '12px 0 14px', fontSize: 13, fontWeight: 800, color: '#d97706', display: 'flex', alignItems: 'center', gap: 10, animation: 'slPop .5s cubic-bezier(.16,1,.3,1)' }}>
                    <span style={{ fontSize: 16, color: '#fbbf24' }}>★</span>Day complete — bonus {dv.bonusEarnedLabel} earned.
                  </div>
                )}
              </div>

              {dv.tomorrow && (
                <div style={{ background: T.gradInset, borderRadius: 18, padding: '14px 18px', marginBottom: 18 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: T.purple600, marginBottom: 3 }}>Tomorrow</div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.6, color: T.ink700, fontWeight: 500 }}>{dv.tomorrow}</div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                {dv.hasPrev && (
                  <a onClick={dv.prev} style={{ fontSize: 12.5, fontWeight: 700, cursor: 'pointer', textDecoration: 'none', padding: '10px 6px' }}>‹ {dv.prevLabel}</a>
                )}
                <span style={{ flex: 1 }} />
                {dv.hasNext && (
                  <a onClick={dv.next} style={{ fontSize: 12.5, fontWeight: 700, cursor: 'pointer', textDecoration: 'none', padding: '10px 6px' }}>{dv.nextLabel} ›</a>
                )}
              </div>
            </div>
          )}

          {/* ============ WEEKS ============ */}
          {isWeeks && (
            <div style={{ animation: 'slFadeUp .45s cubic-bezier(.16,1,.3,1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <a onClick={goHome} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }}>
                  <BackArrow />Home
                </a>
                <span style={{ fontSize: 14, fontWeight: 800 }}>All 14 weeks</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#d97706' }}><span style={{ color: '#fbbf24' }}>★</span> {disp}</span>
              </div>
              {weeksList.map((w) => (
                <div key={w.n} style={{ background: '#fff', borderRadius: 20, boxShadow: T.shadowCard, marginBottom: 10, overflow: 'hidden', opacity: w.op }}>
                  <div onClick={w.toggle} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '15px 18px', cursor: w.locked ? 'default' : 'pointer' }}>
                    {w.past && (
                      <div style={{ width: 34, height: 34, borderRadius: '50%', background: T.purple100, color: T.purple700, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', fontSize: 12.5, fontWeight: 800 }}>{w.n}</div>
                    )}
                    {w.cur && (
                      <div style={{ width: 34, height: 34, borderRadius: '50%', background: T.gradCta, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', fontSize: 12.5, fontWeight: 800, boxShadow: T.shadowCtaSm }}>{w.n}</div>
                    )}
                    {w.locked && (
                      <div style={{ width: 34, height: 34, borderRadius: '50%', background: T.neutral100, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#9691a8">
                          <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" />
                        </svg>
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.2px', color: w.titleColor }}>{w.title}</div>
                      <div style={{ fontSize: 11.5, color: T.ink300, marginTop: 1 }}>{w.sub}</div>
                    </div>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: w.tagColor, flex: 'none' }}>{w.tag}</span>
                  </div>
                  {w.open && (
                    <div style={{ padding: '0 18px 10px' }}>
                      {w.note && (
                        <div style={{ fontSize: 11.5, color: T.ink400, background: T.neutral100, borderRadius: 10, padding: '7px 12px', marginBottom: 4 }}>{w.note}</div>
                      )}
                      {w.rows.map((r, i) => (
                        <div key={i} onClick={r.go} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 0', borderTop: `1px solid ${T.borderNeutral}`, cursor: 'pointer' }}>
                          {r.done && (
                            <div style={{ width: 22, height: 22, borderRadius: '50%', background: T.gradCta, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                              <CheckIcon size={12} />
                            </div>
                          )}
                          {r.notDone && (
                            <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${T.neutral200}`, flex: 'none' }} />
                          )}
                          <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: T.ink700 }}>{r.title}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: r.tagColor }}>{r.tag}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ============ CELEBRATION ============ */}
          {!!cbW && (
            <div onClick={() => this.setState({ celebrate: null })} style={{ position: 'fixed', inset: 0, background: 'rgba(26,12,56,.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, animation: 'slFadeIn .3s ease-out', cursor: 'pointer' }}>
              <div style={{ textAlign: 'center', position: 'relative', padding: 36 }}>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  <div style={{ width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle,rgba(251,191,36,.32),rgba(251,191,36,0) 65%)', animation: 'slPulse 1.8s infinite' }} />
                </div>
                <span style={{ position: 'absolute', left: '14%', top: '14%', color: '#fbbf24', fontSize: 20, animation: 'slRise 1.5s ease-out .2s infinite' }}>★</span>
                <span style={{ position: 'absolute', right: '12%', top: '24%', color: '#fbbf24', fontSize: 14, animation: 'slRise 1.5s ease-out .7s infinite' }}>★</span>
                <span style={{ position: 'absolute', left: '24%', bottom: '26%', color: '#fbbf24', fontSize: 12, animation: 'slRise 1.5s ease-out 1s infinite' }}>★</span>
                <svg width="132" height="156" viewBox="-13 -14.5 26 30" style={{ position: 'relative', animation: 'slShieldIn .7s cubic-bezier(.16,1,.3,1)' }}>
                  <path d="M0 -7.5 L6.4 -4.8 V0.6 C6.4 4.8 3.3 7 0 8.6 C-3.3 7 -6.4 4.8 -6.4 0.6 V-4.8 Z" fill={cbD.fill} stroke="#fbbf24" strokeWidth="0.7" />
                  {cbD.star && <polygon points={this.starPts(0, 0.9, 3.4)} fill="#fbbf24" />}
                  {cbD.chev && <path d="M-3.6 3.4 L0 5.6 L3.6 3.4" fill="none" stroke="#fbbf24" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />}
                  {cbD.laur && (
                    <>
                      <path d="M-8.4 4.5 C-10.2 1.5 -10.2 -2.5 -8.4 -5.5" fill="none" stroke="#fbbf24" strokeWidth="1.2" strokeLinecap="round" />
                      <path d="M8.4 4.5 C10.2 1.5 10.2 -2.5 8.4 -5.5" fill="none" stroke="#fbbf24" strokeWidth="1.2" strokeLinecap="round" />
                    </>
                  )}
                  {cbD.crown && <path d="M-4.2 -9 L-2.6 -11.6 L0 -9.4 L2.6 -11.6 L4.2 -9 Z" fill="#fbbf24" />}
                </svg>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#fbbf24', letterSpacing: '-0.3px', marginTop: 8, position: 'relative' }}>{cbD.name} earned</div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(255,255,255,.78)', marginTop: 6, maxWidth: 280, position: 'relative' }}>
                  Week {cbW || cw} defended with ★ {weekStars} this week.{nextUp ? ' Your shield upgrades at week ' + nextUp + '.' : ''}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginTop: 16, position: 'relative' }}>Tap anywhere to continue</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ActionPlan;
